import { createAsyncThunk } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import axios, { AxiosError } from 'axios';
import type { RootState } from '../store/index';
import {
  SubscriptionPlan,
  OrganizationSubscription,
  PlanFeature,
  PaymentHistory,
  BillingInvoice,
  IBillingSummary,
  InitiatePaymentRequest,
  InitiatePaymentResponse,
  GracePeriodStatus,
  TrialStatus,
  fetchSubscriptionPlans as fetchPlansService,
  fetchPlanDetails as fetchPlanDetailsService,
  fetchCurrentSubscription as fetchCurrentSubscriptionService,
  fetchOrganizationSubscription as fetchOrgSubscriptionService,
  createSubscription as createSubscriptionService,
  updateSubscription as updateSubscriptionService,
  cancelSubscription as cancelSubscriptionService,
  fetchSubscriptionFeatures as fetchFeaturesService,
  checkFeature as checkFeatureService,
  initiatePayment as initiatePaymentService,
  fetchPaymentHistory as fetchPaymentHistoryService,
  fetchPaymentDetails,
  fetchPaymentInvoice,
  fetchBillingInvoiceById as fetchBillingInvoiceByIdService,
  convertTrialToPaid as convertTrialService,
  fetchMyBillingHistory as fetchMyBillingHistoryService,
  fetchMyBillingSummary as fetchMyBillingSummaryService,
  fetchMyGracePeriodStatus as fetchMyGracePeriodStatusService,
  fetchMyTrialStatus as fetchMyTrialStatusService,
  fetchMySubscriptionFeatures as fetchMySubscriptionFeaturesService,
  calculateTrialDisplayDaysRemaining,
} from '../../services/subscription.service';

interface ErrorResponse {
  status: number;
  message: string;
}

/**
 * Fetch all subscription plans
 */
export const fetchSubscriptionPlans = createAsyncThunk<
  { data: SubscriptionPlan[] },
  void,
  { rejectValue: ErrorResponse }
>('subscription/fetchPlans', async (_, { rejectWithValue }) => {
  try {
    const response = await fetchPlansService();
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_plans', 'Failed to fetch plans'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch plan details by ID or slug
 */
export const fetchPlanDetails = createAsyncThunk<
  { data: SubscriptionPlan },
  string | number,
  { rejectValue: ErrorResponse }
>('subscription/fetchPlanDetails', async (idOrSlug, { rejectWithValue }) => {
  try {
    const response = await fetchPlanDetailsService(idOrSlug);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_plan_details', 'Failed to fetch plan details'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch current user's organization subscription
 */
export const fetchCurrentSubscription = createAsyncThunk<
  { data: OrganizationSubscription },
  void,
  { rejectValue: ErrorResponse }
>('subscription/fetchCurrent', async (_, { rejectWithValue }) => {
  try {
    const response = await fetchCurrentSubscriptionService();
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_subscription', 'Failed to fetch subscription'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch organization subscription by ID
 */
export const fetchOrganizationSubscription = createAsyncThunk<
  { data: OrganizationSubscription },
  number,
  { rejectValue: ErrorResponse }
>('subscription/fetchOrganization', async (organizationId, { rejectWithValue }) => {
  try {
    const response = await fetchOrgSubscriptionService(organizationId);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t(
            'toast.failed_to_fetch_organization_subscription',
            'Failed to fetch organization subscription',
          ),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Create new subscription (starts trial)
 */
export const createSubscription = createAsyncThunk<
  { data: OrganizationSubscription },
  { organization_id: string; plan_id: string },
  { rejectValue: ErrorResponse }
>('subscription/create', async (data, { rejectWithValue }) => {
  try {
    const response = await createSubscriptionService(data);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_create_subscription', 'Failed to create subscription'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Update subscription
 */
export const updateSubscription = createAsyncThunk<
  { data: OrganizationSubscription },
  { organizationId: string; data: { plan_id?: string; status?: string; auto_renew?: boolean } },
  { rejectValue: ErrorResponse }
>('subscription/update', async ({ organizationId, data }, { rejectWithValue }) => {
  try {
    const response = await updateSubscriptionService(organizationId, data);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_update_subscription', 'Failed to update subscription'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Cancel subscription
 */
export const cancelSubscription = createAsyncThunk<
  { data: OrganizationSubscription },
  number,
  { rejectValue: ErrorResponse }
>('subscription/cancel', async (organizationId, { rejectWithValue }) => {
  try {
    const response = await cancelSubscriptionService(organizationId);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_cancel_subscription', 'Failed to cancel subscription'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch subscription features
 */
export const fetchSubscriptionFeatures = createAsyncThunk<
  { data: { features: PlanFeature[]; featuresByCategory: Record<string, PlanFeature[]> } },
  number,
  { rejectValue: ErrorResponse }
>('subscription/fetchFeatures', async (organizationId, { rejectWithValue }) => {
  try {
    const response = await fetchFeaturesService(organizationId);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_features', 'Failed to fetch features'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Check if feature is available
 */
export const checkFeature = createAsyncThunk<
  { data: { hasFeature: boolean; feature?: PlanFeature } },
  { organizationId: number; featureCode: string },
  { rejectValue: ErrorResponse }
>('subscription/checkFeature', async ({ organizationId, featureCode }, { rejectWithValue }) => {
  try {
    const response = await checkFeatureService(organizationId, featureCode);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_check_feature', 'Failed to check feature'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Initiate payment
 */
export const initiatePayment = createAsyncThunk<
  { data: InitiatePaymentResponse },
  InitiatePaymentRequest,
  { rejectValue: ErrorResponse }
>('subscription/initiatePayment', async (data, { rejectWithValue }) => {
  try {
    const response = await initiatePaymentService(data);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_initiate_payment', 'Failed to initiate payment'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch payment history
 */
export const fetchPaymentHistory = createAsyncThunk<
  { data: PaymentHistory[] },
  number,
  { rejectValue: ErrorResponse }
>('subscription/fetchPaymentHistory', async (subscriptionId, { rejectWithValue }) => {
  try {
    const response = await fetchPaymentHistoryService(subscriptionId);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_payment_history', 'Failed to fetch payment history'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch a single payment record
 */
export const fetchPaymentById = createAsyncThunk<
  { data: PaymentHistory },
  string,
  { rejectValue: ErrorResponse }
>('subscription/fetchPaymentById', async (paymentId, { rejectWithValue }) => {
  try {
    const response = await fetchPaymentDetails(paymentId);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_payment', 'Failed to fetch payment'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch invoice payload for a payment
 */
export const fetchInvoiceByPaymentId = createAsyncThunk<
  { data: any },
  string,
  { rejectValue: ErrorResponse }
>('subscription/fetchInvoice', async (paymentId, { rejectWithValue }) => {
  try {
    const response = await fetchPaymentInvoice(paymentId);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_invoice', 'Failed to fetch invoice'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch single billing invoice (for receipt view)
 */
export const fetchBillingInvoiceById = createAsyncThunk<
  { data: BillingInvoice },
  string,
  { rejectValue: ErrorResponse }
>('subscription/fetchBillingInvoiceById', async (invoiceId, { rejectWithValue }) => {
  try {
    const response = await fetchBillingInvoiceByIdService(invoiceId);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_billing_invoice', 'Failed to fetch billing invoice'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Convert trial to paid subscription
 */
export const convertTrialToPaid = createAsyncThunk<
  { data: OrganizationSubscription },
  number,
  { rejectValue: ErrorResponse }
>('subscription/convertTrial', async (subscriptionId, { rejectWithValue }) => {
  try {
    const response = await convertTrialService(subscriptionId);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_convert_trial', 'Failed to convert trial'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch billing history for current user's organization (JWT-based, no org ID needed)
 * Standardized approach - uses same pattern as other features
 */
export const fetchMyBillingHistory = createAsyncThunk<
  { data: BillingInvoice[] },
  void,
  { rejectValue: ErrorResponse }
>('subscription/fetchMyBillingHistory', async (_, { rejectWithValue }) => {
  try {
    const response = await fetchMyBillingHistoryService();
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_billing_history', 'Failed to fetch billing history'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch billing summary for current user's organization (JWT-based, no org ID needed)
 * Standardized approach - uses same pattern as other features
 */
export const fetchMyBillingSummary = createAsyncThunk<
  IBillingSummary,
  void,
  { rejectValue: ErrorResponse }
>('subscription/fetchMyBillingSummary', async (_, { rejectWithValue }) => {
  try {
    const response = await fetchMyBillingSummaryService();
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_billing_summary', 'Failed to fetch billing summary'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch grace period status for current user's organization (JWT-based, no org ID needed)
 * Standardized approach - uses same pattern as other features
 */
export const fetchMyGracePeriodStatus = createAsyncThunk<
  GracePeriodStatus,
  void,
  { rejectValue: ErrorResponse }
>('subscription/fetchMyGracePeriodStatus', async (_, { rejectWithValue }) => {
  try {
    const response = await fetchMyGracePeriodStatusService();
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t(
            'toast.failed_to_fetch_grace_period_status',
            'Failed to fetch grace period status',
          ),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch trial status for current user's organization.
 * Derives trial status from existing currentSubscription in Redux state — no extra API call.
 * The data is already available because SubscriptionGuard always keeps currentSubscription fresh.
 */
export const fetchMyTrialStatus = createAsyncThunk<
  TrialStatus,
  void,
  { rejectValue: ErrorResponse; state: RootState }
>('subscription/fetchMyTrialStatus', async (_, { getState, rejectWithValue }) => {
  try {
    const sub = getState().subscription.currentSubscription;
    if (!sub) {
      // No subscription in state yet — fall back to the API
      const response = await fetchMyTrialStatusService();
      return response;
    }

    if (!sub.is_trial) {
      return { isTrial: false, trialDaysRemaining: null, trialEndsAt: null, severity: null };
    }

    const rawEndDate = (sub as any).trialEndDate || (sub as any).trial_end_date || null;
    if (!rawEndDate) {
      return { isTrial: true, trialDaysRemaining: null, trialEndsAt: null, severity: 'info' };
    }

    const daysRemaining = calculateTrialDisplayDaysRemaining(rawEndDate as string) ?? 0;
    let severity: 'info' | 'warning' | 'critical' = 'info';
    if (daysRemaining <= 2) severity = 'critical';
    else if (daysRemaining <= 5) severity = 'warning';

    return {
      isTrial: true,
      trialDaysRemaining: Math.max(0, daysRemaining),
      trialEndsAt: rawEndDate,
      severity,
    };
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_trial_status', 'Failed to fetch trial status'),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Fetch subscription features for current user's organization (JWT-based, no org ID needed)
 * Standardized approach - uses same pattern as other features
 */
export const fetchMySubscriptionFeatures = createAsyncThunk<
  { data: { features: PlanFeature[]; featuresByCategory: Record<string, PlanFeature[]> } },
  void,
  { rejectValue: ErrorResponse }
>('subscription/fetchMyFeatures', async (_, { rejectWithValue }) => {
  try {
    const response = await fetchMySubscriptionFeaturesService();
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t(
            'toast.failed_to_fetch_subscription_features',
            'Failed to fetch subscription features',
          ),
      });
    }
    return rejectWithValue({
      status: 500,
      message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

export const subscriptionActions = {
  fetchSubscriptionPlans,
  fetchPlanDetails,
  fetchCurrentSubscription,
  fetchOrganizationSubscription,
  createSubscription,
  updateSubscription,
  cancelSubscription,
  fetchSubscriptionFeatures,
  checkFeature,
  initiatePayment,
  fetchPaymentHistory,
  fetchPaymentById,
  fetchInvoiceByPaymentId,
  fetchBillingInvoiceById,
  convertTrialToPaid,
  // JWT-based thunks (standardized approach)
  fetchMyBillingHistory,
  fetchMyBillingSummary,
  fetchMyGracePeriodStatus,
  fetchMySubscriptionFeatures,
};
